import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatePresence, m } from 'motion/react';
import { AlertTriangle, CalendarDays, Flag, Folder, Layers, MoreHorizontal, Shapes } from 'lucide-react';
import { Button } from '../components/ui/button';
import BlockedByYouCard from '../components/work/BlockedByYouCard';
import ControlLink from '../components/work/ControlLink';
import { changeKey, DoneSection } from '../components/work/DoneSection';
import ItemContextMenu from '../components/work/ItemContextMenu';
import ListFilterBar from '../components/work/ListFilterBar';
import { MyRequestsSection, NeedsApprovalCard, useMyRequests } from '../components/work/MyApprovals';
import NewWorkItemMenu from '../components/work/NewWorkItemMenu';
import ShowSubtasksCheckbox from '../components/work/ShowSubtasksCheckbox';
import Peek from '../components/work/Peek';
import SetDueDate from '../components/work/SetDueDate';
import StatusControl, { finishState } from '../components/work/StatusControl';
import CompleteButton from '../components/work/CompleteButton';
import WorkKanban from '../components/work/WorkKanban';
import WorkLayoutSwitch from '../components/work/WorkLayoutSwitch';
import { PageFrame } from '../components/work/page-chrome';
import { ErrorAlert, ListEmpty, LoadingRows } from '../components/work/page-states';
import { useOverlays } from '../components/work/overlay-context';
import { useDirectoryQuery } from '../components/work/use-directory-query';
import { useSession } from '../components/session-context';
import { useMembers } from '../components/wiki/use-member-names';
import { apiFetch, v1Path } from '../lib/api';
import { isOpenAssignedStateGroup, sortQueueItems, startOfDay } from '../lib/assigned-queue';
import { formatFriendlyDateOnly, lateText } from '../lib/dates';
import { fetchInitiativeWorkspace, initiativePath, INITIATIVES_PATH } from '../lib/initiatives';
import { blockerSummary, groupByWhen, type WhenGroupId } from '../lib/my-tasks';
import { projectWorkPath } from '../lib/project-path';
import { cn } from '../lib/utils';
import type { FilterSpec } from '../lib/filters';
import { fetchStatesByProjectIds } from '../lib/project-states';
import { ASSIGNED_ORDER_OPTIONS } from '../lib/work-list-sort';
import { filterWorkItemRows } from '../lib/work-item-list';
import {
  STATE_GROUP_BOARD_COLUMNS,
  WORK_ITEM_TYPES,
  memberDisplayName,
  workItemTypeLabel,
  workItemSubtypeLabel,
  type ProjectDto,
  type StateDto,
  type WorkItemSlim,
  type WorkItemSubtypeDefinition,
} from '../lib/work-types';

const ASSIGNED_CHIPS = ['project', 'type', 'subtype', 'priority', 'due'] as const;
const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'none', label: 'None' },
] as const;
const DUE_OPTIONS = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'this_week', label: 'This week' },
  { value: 'no_due', label: 'No due date' },
] as const;

type ViewDto = {
  id: string;
  name: string;
  layout: string;
  project_id: string | null;
  filters: FilterSpec;
  is_system: boolean;
};

type ListResponse = {
  items: WorkItemSlim[];
  page: { limit: number; offset: number; total: number };
};

export default function AssignedToMe() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useDirectoryQuery(ASSIGNED_CHIPS, 'due_date');
  const showSubtasks = searchParams.get('show_sub_items') === 'true';
  const [items, setItems] = useState<WorkItemSlim[]>([]);
  const [projectNames, setProjectNames] = useState<Map<string, string>>(new Map());
  const [subtypesByProject, setSubtypesByProject] = useState<Map<string, WorkItemSubtypeDefinition[]>>(
    new Map(),
  );
  const [stateById, setStateById] = useState<Map<string, StateDto>>(() => new Map());
  const [statesByProject, setStatesByProject] = useState<Map<string, StateDto[]>>(() => new Map());
  const [groupByStateId, setGroupByStateId] = useState<Map<string, string>>(() => new Map());
  const [initiativeNames, setInitiativeNames] = useState<Map<string, string>>(() => new Map());
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isKanban = searchParams.get('layout') === 'kanban';
  const { userId } = useSession();
  const { toast } = useOverlays();
  const members = useMembers();
  const requests = useMyRequests();

  const load = useCallback(async () => {
    setError(null);
    try {
      const [listed, projectRes] = await Promise.all([
        apiFetch<{ items: ViewDto[] }>(v1Path('/views')),
        apiFetch<{ items: ProjectDto[] }>(v1Path('/projects?limit=100&offset=0')),
      ]);
      setProjectNames(new Map(projectRes.items.map((project) => [project.id, project.name])));
      setSubtypesByProject(
        new Map(projectRes.items.map((project) => [project.id, project.subtypes ?? []])),
      );
      const assigned = listed.items.find((view) => view.name === 'Assigned to me' && view.is_system);
      if (!assigned) {
        setError('Assigned to me view is missing.');
        setItems([]);
        setTotal(0);
        return;
      }
      const list = await apiFetch<ListResponse>(
        `${v1Path(`/views/${assigned.id}/execute`)}?limit=100&offset=0`,
      );
      // Service Requests have their own Home cards (approvals, your requests); this list is stream work.
      const work = list.items.filter((item) => item.type !== 'service_request');
      const states = await fetchStatesByProjectIds(
        Array.from(new Set(work.map((item) => item.project_id))),
      );
      const groups = new Map<string, string>();
      Array.from(states.byId.entries()).forEach(([id, state]) => {
        groups.set(id, state.group);
      });
      const open = work.filter((item) => isOpenAssignedStateGroup(groups.get(item.state_id)));
      setItems(open);
      setTotal(open.length);
      setStateById(states.byId);
      setStatesByProject(states.byProject);
      setGroupByStateId(groups);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assigned work');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const onChanged = () => void load();
    window.addEventListener('bb:work-items-changed', onChanged);
    return () => window.removeEventListener('bb:work-items-changed', onChanged);
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchInitiativeWorkspace()
      .then((workspace) =>
        apiFetch<ListResponse>(
          `${v1Path(`/projects/${workspace.project.id}/work-items`)}?types=initiative&show_sub_items=false&limit=100&offset=0`,
        ),
      )
      .then((res) => {
        if (!cancelled) setInitiativeNames(new Map(res.items.map((initiative) => [initiative.id, initiative.title])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const moveItem = useCallback(
    async (item: WorkItemSlim, stateId: string) => {
      await apiFetch(v1Path(`/projects/${item.project_id}/work-items/${item.id}/move`), {
        method: 'POST',
        body: JSON.stringify({ version: item.version, state_id: stateId }),
      });
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
    },
    [],
  );

  const changeStatus = useCallback(
    async (item: WorkItemSlim, stateId: string) => {
      if (stateId === item.state_id) return;
      try {
        await moveItem(item, stateId);
        toast(`Status changed to ${stateById.get(stateId)?.name ?? 'the new status'}`);
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not change the status');
      }
      void load();
    },
    [load, moveItem, stateById, toast],
  );

  const complete = useCallback(
    async (item: WorkItemSlim) => {
      await new Promise((resolve) => window.setTimeout(resolve, 280));
      setItems((curr) => curr.filter((row) => row.id !== item.id));
      try {
        if (item.type === 'initiative') {
          const done = finishState(statesByProject.get(item.project_id) ?? []);
          if (!done) throw new Error('This stream has no finished status.');
          await moveItem(item, done.id);
        } else {
          await apiFetch(v1Path(`/projects/${item.project_id}/work-items/${item.id}/archive`), {
            method: 'POST',
            body: JSON.stringify({ version: item.version }),
          });
          window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
        }
        toast(`Marked as done: ${item.title}`);
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not mark it done');
        void load();
      }
    },
    [load, moveItem, statesByProject, toast],
  );

  const setDueDate = useCallback(
    async (item: WorkItemSlim, dueDate: string) => {
      try {
        await apiFetch(v1Path(`/projects/${item.project_id}/work-items/${item.id}`), {
          method: 'PATCH',
          body: JSON.stringify({ version: item.version, due_date: dueDate }),
        });
        toast(`Due date set to ${formatFriendlyDateOnly(dueDate, startOfDay(new Date())) ?? dueDate}`);
        window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
        void load();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not set the due date');
        throw err;
      }
    },
    [load, toast],
  );

  const nameOf = useCallback(
    (id: string) => {
      const member = members.get(id);
      return member ? memberDisplayName(member) : null;
    },
    [members],
  );


  const today = useMemo(() => startOfDay(new Date()), []);

  const view = useMemo(() => {
    const filtered = filterWorkItemRows(items, {
      query: filters.query,
      types: filters.selected.type,
      subtypes: filters.selected.subtype,
      priorities: filters.selected.priority,
      projectIds: filters.selected.project,
      dueBuckets: filters.selected.due,
      today,
      showSubItems: showSubtasks,
    });
    return { ranked: sortQueueItems(filtered, isKanban ? filters.orderBy : 'due_date', today) };
  }, [filters.orderBy, filters.query, filters.selected, isKanban, items, showSubtasks, today]);


  const projectOptions = useMemo(() => {
    const ids = Array.from(new Set(items.map((item) => item.project_id)));
    return ids
      .map((id) => ({ value: id, label: projectNames.get(id) ?? id }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [items, projectNames]);

  const subtypeOptions = useMemo(() => {
    const namesByKey = new Map<string, Set<string>>();
    for (const item of items) {
      for (const subtype of subtypesByProject.get(item.project_id) ?? []) {
        const names = namesByKey.get(subtype.key) ?? new Set<string>();
        names.add(subtype.name);
        namesByKey.set(subtype.key, names);
      }
    }
    return Array.from(namesByKey, ([value, names]) => ({
      value,
      label: Array.from(names).join(' / '),
    })).sort((left, right) => left.label.localeCompare(right.label));
  }, [items, subtypesByProject]);


  function layoutSearch(next: 'list' | 'kanban'): string {
    const params = new URLSearchParams(searchParams);
    if (next === 'kanban') params.set('layout', 'kanban');
    else params.delete('layout');
    return params.toString();
  }

  const peekKeys = isKanban
    ? STATE_GROUP_BOARD_COLUMNS.flatMap((column) =>
        view.ranked
          .filter((item) => (groupByStateId.get(item.state_id) ?? item.state_id) === column.id)
          .map((item) => item.key),
      )
    : view.ranked.map((item) => item.key);


  const header = (
    <MyWorkHeader
      action={
        <div className="flex items-center gap-2">
          <WorkLayoutSwitch isKanban={isKanban} listSearch={layoutSearch('list')} boardSearch={layoutSearch('kanban')} />
          <NewWorkItemMenu label="New task" primaryType="task" />
        </div>
      }
    />
  );

  const completed = userId ? (
    <DoneSection
      scope={{ personId: userId }}
      showPeople={false}
      collapsible
      completedTarget="mine"
      empty="Nothing marked as done yet."
      refreshKey={changeKey(items)}
    />
  ) : null;

  if (!loading && !error && total === 0) {
    return (
      <PageFrame>
        <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-5">
          {header}
          <NeedsApprovalCard />
          <BlockedByYouCard />
          <p role="status" className="py-12 text-center text-base text-muted-foreground">
            You have no open tasks right now.
          </p>
          <MyRequestsSection items={requests} />
          {completed}
          <Peek itemKeys={peekKeys} />
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <div className={cn('space-y-5', !isKanban && 'mx-auto w-full max-w-5xl')}>
        {header}

        <NeedsApprovalCard />
        <BlockedByYouCard />

        <ListFilterBar
          query={filters.query}
          onQueryChange={(query) => filters.apply({ query })}
          searchPlaceholder="Search your tasks..."
          searchAriaLabel="Search your tasks"
          chips={[
            ...(projectOptions.length > 0
              ? [
                  {
                    id: 'project',
                    label: 'Stream',
                    icon: Folder,
                    options: projectOptions,
                    selected: filters.selected.project,
                    onToggle: (value: string) => filters.toggleChip('project', value),
                  },
                ]
              : []),
            {
              id: 'type',
              label: 'Type',
              icon: Layers,
              options: WORK_ITEM_TYPES
                .filter((type) => type !== 'service_request')
                .map((type) => ({ value: type, label: workItemTypeLabel(type) })),
              selected: filters.selected.type,
              onToggle: (value) => filters.toggleChip('type', value),
            },
            ...(subtypeOptions.length > 0
              ? [
                  {
                    id: 'subtype',
                    label: 'Task type',
                    icon: Shapes,
                    options: subtypeOptions,
                    selected: filters.selected.subtype,
                    onToggle: (value: string) => filters.toggleChip('subtype', value),
                  },
                ]
              : []),
            {
              id: 'priority',
              label: 'Priority',
              icon: Flag,
              options: [...PRIORITY_OPTIONS],
              selected: filters.selected.priority,
              onToggle: (value) => filters.toggleChip('priority', value),
            },
            {
              id: 'due',
              label: 'Due date',
              icon: CalendarDays,
              options: [...DUE_OPTIONS],
              selected: filters.selected.due,
              onToggle: (value) => filters.toggleChip('due', value),
            },
          ]}
          sort={
            isKanban
              ? {
                  value: filters.orderBy ?? 'due_date',
                  options: [...ASSIGNED_ORDER_OPTIONS],
                  onChange: (value) => filters.apply({ orderBy: value }),
                }
              : undefined
          }
          afterChips={
            <ShowSubtasksCheckbox
              id="assigned-show-subtasks"
              checked={showSubtasks}
              onCheckedChange={(checked) => {
                const params = new URLSearchParams(searchParams);
                if (checked) params.set('show_sub_items', 'true');
                else params.delete('show_sub_items');
                setSearchParams(params, { replace: true });
              }}
            />
          }
          onClear={filters.clear}
        />

        {error ? <ErrorAlert>{error}</ErrorAlert> : null}

        {loading ? (
          <LoadingRows columns={7} />
        ) : error ? null : view.ranked.length === 0 ? (
          <ListEmpty
            title="None of your tasks match"
            action={
              <Button type="button" variant="outline" size="sm" onClick={filters.clear}>
                Clear filters
              </Button>
            }
          />
        ) : isKanban ? (
          <WorkKanban
            columns={STATE_GROUP_BOARD_COLUMNS.filter((column) => column.id !== 'completed')}
            items={view.ranked}
            showEmptyGroups
            canReorder={false}
            subtypeLabelForItem={(item) =>
              workItemSubtypeLabel(item.subtype, subtypesByProject.get(item.project_id))
            }
            columnIdForItem={(item) => groupByStateId.get(item.state_id) ?? item.state_id}
            onMove={() => undefined}
          />
        ) : (
          <div className="space-y-6">
            <AnimatePresence initial={false}>
              {groupByWhen(view.ranked, today).map((group) => (
                <m.section
                  key={group.id}
                  aria-labelledby={`when-${group.id}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, transition: { delay: 0.3, duration: 0.2 } }}
                  transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                >
                  <h2
                    id={`when-${group.id}`}
                    className={cn(
                      'mb-2 flex items-baseline gap-2 font-display text-base font-extrabold tracking-tight',
                      group.id === 'late' && 'text-red-700 dark:text-red-300',
                    )}
                  >
                    {group.title}
                    <span className="text-sm font-semibold tabular-nums text-muted-foreground">{group.items.length}</span>
                  </h2>
                  <ul className="divide-y divide-border/70 overflow-hidden rounded-2xl border border-border/80 bg-card shadow-[0_1px_2px_hsl(var(--brand-navy)/0.04),0_4px_12px_-6px_hsl(var(--brand-navy)/0.08)]">
                    <AnimatePresence initial={false}>
                      {group.items.map((item) => (
                        <TaskRow
                          key={item.id}
                          item={item}
                          group={group.id}
                          today={today}
                          state={stateById.get(item.state_id)}
                          states={statesByProject.get(item.project_id) ?? []}
                          stream={projectNames.get(item.project_id)}
                          initiative={item.initiative_id ? initiativeNames.get(item.initiative_id) : undefined}
                          blocker={blockerSummary(item, nameOf, userId, today)}
                          onComplete={() => complete(item)}
                          onStatus={(stateId) => void changeStatus(item, stateId)}
                          onDueDate={(dueDate) => setDueDate(item, dueDate)}
                          onChanged={() => void load()}
                          onRemoved={(id) => setItems((curr) => curr.filter((row) => row.id !== id))}
                        />
                      ))}
                    </AnimatePresence>
                  </ul>
                </m.section>
              ))}
            </AnimatePresence>
          </div>
        )}

        <MyRequestsSection items={requests} />

        {completed}

        <Peek itemKeys={peekKeys} />
      </div>
    </PageFrame>
  );
}


function MyWorkHeader({ action }: { action: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <h1 data-slot="page-title" className="truncate text-[22px] font-semibold leading-7 tracking-tight">
        My work
      </h1>
      {action}
    </div>
  );
}

const CONTEXT_LINK_CLASS = 'truncate text-muted-foreground no-underline hover:text-foreground hover:underline';

const TITLE_CLASS = 'block truncate text-sm font-medium text-foreground no-underline hover:text-primary hover:no-underline';

function TaskRow({
  item,
  group,
  today,
  state,
  states,
  stream,
  initiative,
  blocker,
  onComplete,
  onStatus,
  onDueDate,
  onChanged,
  onRemoved,
}: {
  item: WorkItemSlim;
  group: WhenGroupId;
  today: Date;
  state: StateDto | undefined;
  states: StateDto[];
  stream: string | undefined;
  initiative: string | undefined;
  blocker: ReturnType<typeof blockerSummary>;
  onComplete: () => Promise<void>;
  onStatus: (stateId: string) => void;
  onDueDate: (dueDate: string) => Promise<void>;
  onChanged: () => void;
  onRemoved: (id: string) => void;
}) {
  const when =
    group === 'late'
      ? lateText(item.due_date, today)
      : group === 'no_date'
        ? null
        : formatFriendlyDateOnly(item.due_date, today);
  const urgent = item.priority === 'urgent' || item.priority === 'high';
  const isInitiative = item.type === 'initiative';
  const blockedDays = blocker?.days == null ? '' : blocker.days === 0 ? ' since today' : ` for ${blocker.days} ${blocker.days === 1 ? 'day' : 'days'}`;
  return (
    <m.li
      id={`issue-${item.id}`}
      tabIndex={-1}
      className={cn(
        'overflow-hidden transition-colors',
        blocker
          ? 'bg-red-50 hover:bg-red-100/70 dark:bg-red-950/40 dark:hover:bg-red-950/60'
          : 'hover:bg-muted/30',
      )}
      exit={{
        opacity: 0,
        x: 28,
        height: 0,
        transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1], height: { delay: 0.12, duration: 0.24 } },
      }}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <CompleteButton title={item.title} disabled={item.type === 'initiative' && !finishState(states)} onComplete={onComplete} />
        <div className="min-w-0 flex-1">
          {isInitiative ? (
            <Link to={initiativePath(item.id)} className={TITLE_CLASS}>
              {item.title}
            </Link>
          ) : (
            <ControlLink workItemId={item.id} workItemKey={item.key} projectId={item.project_id} className={TITLE_CLASS}>
              {item.title}
            </ControlLink>
          )}
          <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs">
            {initiative && item.initiative_id ? (
              <>
                <Link to={initiativePath(item.initiative_id)} className={CONTEXT_LINK_CLASS}>
                  {initiative}
                </Link>
                {stream ? <span className="text-muted-foreground/60" aria-hidden>·</span> : null}
              </>
            ) : null}
            {stream ? (
              <Link to={isInitiative ? INITIATIVES_PATH : projectWorkPath(item.project_id)} className={CONTEXT_LINK_CLASS}>
                {stream}
              </Link>
            ) : null}
            {urgent ? (
              <span className="shrink-0 font-medium text-red-700 dark:text-red-300">
                {' · '}
                {item.priority === 'urgent' ? 'Urgent' : 'High priority'}
              </span>
            ) : null}
          </p>
        </div>
        {state ? (
          <span className="hidden w-32 shrink-0 sm:inline-flex">
            <StatusControl stateId={item.state_id} states={states} blocked={Boolean(blocker)} readOnly={false} compact onChange={onStatus} />
          </span>
        ) : null}
        <span
          className={cn(
            'flex shrink-0 justify-end text-right text-sm font-medium tabular-nums sm:w-36',
            group === 'late' ? 'text-red-700 dark:text-red-300' : 'text-foreground',
          )}
        >
          {group === 'no_date' ? <SetDueDate today={today} label={item.title} onSet={onDueDate} /> : when}
        </span>
        <ItemContextMenu
          projectId={item.project_id}
          workItemId={item.id}
          workItemKey={item.key}
          version={item.version}
          states={states}
          currentStateId={item.state_id}
          blockedReason={item.blocked_reason ?? null}
          itemType={item.type}
          onChanged={onChanged}
          onRemoved={onRemoved}
        >
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" aria-label={`Actions for ${item.key}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </ItemContextMenu>
      </div>
      {blocker ? (
        <div className="mx-4 mb-3 flex items-start gap-2.5 rounded-lg border border-red-200 bg-white px-3 py-2 text-red-950 sm:ml-12 dark:border-red-900 dark:bg-red-950/30 dark:text-red-100">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 text-sm font-medium">
              <span className="sr-only">Blocked: </span>
              {blocker.reason}
            </p>
            <p className="mt-0.5 text-xs text-red-800 dark:text-red-200/90">
              {blocker.waitingOn ? `Waiting on ${blocker.waitingOn}${blockedDays}` : `Blocked${blockedDays}`}
              {' · '}Next: {blocker.next}
            </p>
          </div>
          <ControlLink
            workItemId={item.id}
            workItemKey={item.key}
            projectId={item.project_id}
            className="shrink-0 self-center text-sm font-medium text-primary no-underline hover:underline"
          >
            View blocker
          </ControlLink>
        </div>
      ) : null}
    </m.li>
  );
}
